import { useCallback, useContext, useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import "dayjs/locale/it";
import "dayjs/locale/fr";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import { getAnswerValue } from "../../utils/partial";
import BorseContext from "../../context/borse";
import UserContext from "../../context/user";
import Document from "../document";
import { getFilenameDetails } from "../../utils/documents";
import { QUESTION_MAPPING, SCHOOL_QUESTION_MAPPING } from "../../utils/scholarships";
import AccordionBox from "../accordion-box";
dayjs.extend(LocalizedFormat);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);

const BorseBoxWrapper = ({ readOnly }) => {
  const { callApi } = useContext(UserContext);
  const { currentRequest, survey, labels, appLang, schoolType, setCurrentSectionIndex, setPath } =
    useContext(BorseContext);
  const questions = Object.entries(QUESTION_MAPPING);
  const schoolQuestions = useMemo(
    () => Object.entries(SCHOOL_QUESTION_MAPPING?.[schoolType] || {}) || [],
    [schoolType]
  );

  const data = useMemo(
    () =>
      questions?.concat(schoolQuestions)?.map(([key, value]) => {
        const answerValue = getAnswerValue(
          survey,
          currentRequest?.surveySubmission?.answers,
          value.section,
          value.question
        );
        const formattedAnswerValue =
          value.answerType === "date" && answerValue?.answerValue
            ? dayjs(answerValue?.answerValue, "DD-MM-YYYY").locale(appLang?.value).format("L")
            : answerValue?.[value.answerType];
        return [
          key,
          {
            value: formattedAnswerValue,
            behaviour: value.behaviour,
            type: value.answerType,
            box: value.box,
          },
        ];
      }),
    [survey, currentRequest, appLang, questions, schoolQuestions]
  );

  const isAdditionalDocsVisible = [
    "INTEGRATION_SENT",
    "EVALUATION_IN_PROGRESS",
    "ASSIGNED",
    "NOT_ASSIGNED",
  ].includes(currentRequest?.status);

  const {
    data: additionalDocs,
    isError: errorGetDocumentation,
    isFetching: isGetDocumentationFetching,
  } = useQuery(
    ["additional-documents"],
    () => callApi(`/scholarship/request/${currentRequest?.id}/additionalFiles`),
    {
      enabled: isAdditionalDocsVisible,
      select: (data) =>
        data?.map((doc) => ({ ...doc.file, ...getFilenameDetails(doc.file.fileName) })),
    }
  );

  const fileResponses = useQueries({
    queries: data
      ?.filter(([_key, value]) => value.type === "fileId" && !!value.value)
      ?.map(([_key, value]) => ({
        queryKey: ["document", value.value],
        queryFn: () => callApi(`/survey/documents/${value.value}/detail`),
        select: (data) => ({
          ...data,
          ...getFilenameDetails(data.fileName),
        }),
      })),
  });

  const files = useMemo(() => fileResponses?.map((response) => response?.data), [fileResponses]);
  const isFetching = useMemo(
    () => fileResponses?.some((response) => response?.isFetching),
    [fileResponses]
  );

  const onEdit = useCallback(
    (index) => {
      setPath((current) => current.slice(0, index + 1));
      setCurrentSectionIndex(index);
    },
    [setPath, setCurrentSectionIndex]
  );

  const accordionBoxes = useMemo(
    () => [
      {
        title: labels?.WELFARE_BORSE_BOX_USER_DATA || "Dati dipendente",
        editLabel: labels?.WELFARE_BORSE_BOX_CHANGE_USER_DATA || "Modifica dati dipendente",
      },
      {
        title: labels?.WELFARE_BORSE_STUDENT_DATA || "Dati studente",
        editLabel: labels?.WELFARE_BORSE_BOX_CHANGE_STUDENT_DATA || "Modifica dati studente",
      },
      {
        title: labels?.WELFARE_BORSE_SCHOOL_DATA || "Dati scuola e documentazione",
        editLabel: labels?.WELFARE_BORSE_BOX_CHANGE_SCHOOL_DATA || "Modifica dati scuola",
      },
    ],
    [labels]
  );

  return (
    <div className="box-wrapper box-wrapper--accordion">
      {accordionBoxes?.map(
        (accordion, i) =>
          data?.some(([_key, value]) => value.box === i && value.value) && (
            <AccordionBox
              title={accordion.title}
              onEdit={() => onEdit(i)}
              editLabel={accordion.editLabel}
              readOnly={readOnly}
              key={i}
            >
              <span className="box-wrapper__info">
                {data
                  ?.filter(([_key, value]) => value.box === i && value.behaviour === "value-only")
                  ?.map(([_key, value]) => value.value)
                  ?.join(" ")}
              </span>
              {data
                ?.filter(([_key, value]) => value.box === i && value.behaviour !== "value-only")
                ?.map(
                  ([key, value]) =>
                    value.value && (
                      <span
                        className="box-wrapper__info"
                        key={key}
                        data-label={`WELFARE_BORSE_BOX_${key.toUpperCase()}`}
                      >
                        {labels?.[`WELFARE_BORSE_BOX_${key.toUpperCase()}`]}:{" "}
                        {value.type === "fileId" ? (
                          <span className="box-wrapper__info">
                            {isFetching ? (
                              <span className="loading-spinner" />
                            ) : (
                              <Document
                                doc={files?.find(
                                  (file) => Number(file?.id) === Number(value.value)
                                )}
                                removable={false}
                              />
                            )}
                          </span>
                        ) : (
                          <span>{value.value || "-"}</span>
                        )}
                      </span>
                    )
                )}
            </AccordionBox>
          )
      )}
      {isAdditionalDocsVisible &&
        (isGetDocumentationFetching ? (
          <div className="loading-spinner" />
        ) : errorGetDocumentation ? (
          <div className="form__error">Error retriving documentation</div>
        ) : (
          additionalDocs.length > 0 && (
            <AccordionBox
              title={labels?.WELFARE_BORSE_ADDITIONAL_DOCS || "Documentazione aggiuntiva"}
              readOnly={true}
            >
              {additionalDocs?.map?.((doc, i) => (
                <div className="box-wrapper__info" key={i}>
                  {isFetching ? (
                    <span className="loading-spinner" />
                  ) : (
                    <Document doc={doc} removable={false} />
                  )}
                </div>
              ))}
            </AccordionBox>
          )
        ))}
    </div>
  );
};

export default BorseBoxWrapper;
